import { useSession } from "next-auth/react"
import { useRouter } from "next/router"
import { useEffect, useRef } from "react"

export interface TeamEntry {
  _id: string
  name: string
  isActive: boolean
  role: string
  owner: boolean
}

interface ProtectedSessionData {
  user: {
    id: string
    email: string
    name?: string
  }
  user_data: {
    _id: string
    username: string
    email: string
    role: string
    profilePicture?: string
    profilePictureSignedUrl?: string
    groups?: string[]
  }
  team_data: {
    _id: string
    name: string
    createdAt: string
    description?: string
    country?: string
    city?: string
    zip?: string
    street?: string
    vatId?: string
    advisorNumber?: number
    clientNumber?: number
    users?: any[]
  }
  accessToken: string
  refreshToken: string
  rights: string[]
  usage: {
    used: number
    limit: number
    users?: number
  }
  cost: any
  teams: TeamEntry[]
  error?: string
}

export function useProtectedSession() {
  const { data: session, status } = useSession({
    required: false, // Don't use required: true - it causes automatic redirects during session initialization
    // Instead, handle redirects manually in useEffect
  })
  
  const router = useRouter()
  const hasRedirectedRef = useRef(false)

  useEffect(() => {
    // Only redirect if we're sure the user is not authenticated (not loading)
    // and there's a critical error or no session at all
    const criticalErrors = ["RefreshAccessTokenError", "Unauthorized"];
    
    // Don't redirect during loading state - wait for session to initialize
    if (status === "loading") {
      return
    }
    
    // Only redirect if unauthenticated AND not currently on auth pages
    if (status === "unauthenticated" && !hasRedirectedRef.current) {
      const currentPath = router.pathname
      const isAuthPage = currentPath?.startsWith('/auth') || 
                         currentPath?.startsWith('/signup') || 
                         currentPath === '/login' ||
                         currentPath === '/forgot-password'
      
      if (!isAuthPage) {
        console.log("User is not authenticated, redirecting to login")
        hasRedirectedRef.current = true
        router.push("/auth/signin")
      }
      return
    }
    
    // Reset redirect flag if user becomes authenticated
    if (status === "authenticated") {
      hasRedirectedRef.current = false
    }
    
    // Handle critical errors only if authenticated (session exists but has error)
    if (
      status === "authenticated" && 
      session?.error && 
      typeof session.error === 'string' &&
      criticalErrors.includes(session.error) &&
      !hasRedirectedRef.current
    ) {
      console.log("Critical session error, redirecting to login:", session.error)
      hasRedirectedRef.current = true
      router.push("/auth/signin")
    }
  }, [status, session?.error, router])

  // Return session with proper typing and null safety
  if (status === "loading") {
    return { session: null, status: "loading" }
  }

  if (status !== "authenticated" || !session) {
    return { session: null, status: "unauthenticated" }
  }

  const toOptionalNumber = (value: any): number | undefined => {
    if (value === null || value === undefined || value === "") return undefined;
    if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  };

  // Ensure session has required properties with fallbacks
  const protectedSession: ProtectedSessionData = {
    user: {
      id: (session.user as any)?.id || "",
      email: session.user?.email || "",
      name: session.user?.name || ""
    },
    user_data: {
      _id: (session as any).user_data?._id || "",
      username: (session as any).user_data?.username || "",
      email: (session as any).user_data?.email || "",
      role: (session as any).user_data?.role || "USER",
      profilePicture: (session as any).user_data?.profilePicture || "",
      profilePictureSignedUrl: (session as any).user_data?.profilePictureSignedUrl || "",
      groups: (session as any).user_data?.groups || []
    },
    team_data: {
      _id: (session as any).team_data?._id || "",
      name: (session as any).team_data?.name || "",
      createdAt: (session as any).team_data?.createdAt || new Date().toISOString(),
      description: (session as any).team_data?.description || "",
      country: (session as any).team_data?.country || "",
      city: (session as any).team_data?.city || "",
      zip: (session as any).team_data?.zip || "",
      street: (session as any).team_data?.street || "",
      vatId: (session as any).team_data?.vatId || "",
      advisorNumber: toOptionalNumber((session as any).team_data?.advisorNumber),
      clientNumber: toOptionalNumber((session as any).team_data?.clientNumber),
      users: (session as any).team_data?.users || []
    },
    accessToken: (session as any).accessToken || "",
    refreshToken: (session as any).refreshToken || "",
    rights: (session as any).rights || [],
    usage: {
      used: (session as any).usage?.used || 0,
      limit: (session as any).usage?.limit || 0,
      users: typeof (session as any).usage?.users === 'number' ? (session as any).usage.users : undefined,
    },
    cost: (session as any).cost || {},
    teams: (session as any).teams || [],
    error: (session as any).error
  }

  return { session: protectedSession, status: "authenticated" }
}
